// PDF Edit (0.50): as ferramentas que mexem no PDF, feitas no próprio aparelho com o pdf-lib (sem servidor).
// Cada função recebe os bytes do PDF e devolve os bytes do PDF novo (o original nunca é alterado).
import {
  LineCapStyle, PDFArray, PDFCheckBox, PDFDict, PDFDocument, PDFDropdown, PDFName, PDFOptionList, PDFRadioGroup, PDFTextField, StandardFonts, degrees, rgb,
  type PDFFont, type PDFPage, type PDFImage,
} from 'pdf-lib';
import { limparTexto } from './pdfTexto';
import type { Traco } from '../store/types';

export const carregar = (b: Uint8Array) => PDFDocument.load(b, { ignoreEncryption: true, updateMetadata: false, throwOnInvalidObject: false });
const salvar = (d: PDFDocument) => d.save({ useObjectStreams: true });
export const novo = async () => { const d = await PDFDocument.create(); d.setProducer('My Planner Anne'); d.setCreator('My Planner Anne'); return d; };

export const A4: [number, number] = [595.28, 841.89];
export const CARTA: [number, number] = [612, 792];
export const A5: [number, number] = [419.53, 595.28];
export const OFICIO: [number, number] = [612, 1008];

// ─── páginas ───
/** "1-3, 5, 8-" -> índices (começando em 0). Aceita faixa invertida ("5-3") e "8-" (até o fim). */
export function lerPaginas(spec: string, total: number): number[] {
  const out: number[] = [];
  for (const parte of spec.split(/[,;\s]+/).filter(Boolean)) {
    const m = parte.match(/^(\d*)\s*-\s*(\d*)$/);
    if (m) {
      const a = m[1] ? +m[1] : 1, b = m[2] ? +m[2] : total;
      const passo = a <= b ? 1 : -1;
      for (let i = a; passo > 0 ? i <= b : i >= b; i += passo) if (i >= 1 && i <= total) out.push(i - 1);
    } else if (/^\d+$/.test(parte)) { const i = +parte; if (i >= 1 && i <= total) out.push(i - 1); }
  }
  return out;
}
/** "1-3, 4-8" -> grupos (cada faixa vira um arquivo). */
export const gruposDePaginas = (spec: string, total: number) => spec.split(/[,;]+/).map(s => lerPaginas(s, total)).filter(g => g.length);

/** Tamanho que aparece na tela (considera o giro da página). */
export function tamanhoVisivel(p: PDFPage): [number, number] {
  const { width, height } = p.getSize();
  const r = ((p.getRotation().angle % 360) + 360) % 360;
  return r === 90 || r === 270 ? [height, width] : [width, height];
}

export type Posicao = 'sup-esq' | 'sup-centro' | 'sup-dir' | 'meio-esq' | 'centro' | 'meio-dir' | 'inf-esq' | 'inf-centro' | 'inf-dir';
export const POSICOES: Posicao[] = ['sup-esq', 'sup-centro', 'sup-dir', 'meio-esq', 'centro', 'meio-dir', 'inf-esq', 'inf-centro', 'inf-dir'];
/** Canto inferior esquerdo de uma caixa w x h na posição escolhida (margem em pontos). */
export function posicionar(W: number, H: number, w: number, h: number, pos: Posicao, margem = 36) {
  const [v, hz] = pos === 'centro' ? ['meio', 'centro'] : pos.split('-');
  const x = hz === 'esq' ? margem : hz === 'dir' ? W - margem - w : (W - w) / 2;
  const y = v === 'sup' ? H - margem - h : v === 'inf' ? margem : (H - h) / 2;
  return { x, y };
}

export function corRgb(hex: string) {
  const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  return m ? rgb(parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255) : rgb(0, 0, 0);
}

// ─── Organizar ───
export async function juntar(arqs: Uint8Array[]) {
  const out = await novo();
  for (const b of arqs) {
    const d = await carregar(b);
    for (const p of await out.copyPages(d, d.getPageIndices())) out.addPage(p);
  }
  return salvar(out);
}

async function copiar(d: PDFDocument, idx: number[]) {
  const out = await novo();
  for (const p of await out.copyPages(d, idx)) out.addPage(p);
  return salvar(out);
}
export type ModoDividir = { tipo: 'cada' } | { tipo: 'n'; n: number } | { tipo: 'faixas'; spec: string };
export async function dividir(b: Uint8Array, modo: ModoDividir): Promise<{ rotulo: string; bytes: Uint8Array }[]> {
  const d = await carregar(b);
  const total = d.getPageCount();
  let grupos: number[][];
  if (modo.tipo === 'cada') grupos = d.getPageIndices().map(i => [i]);
  else if (modo.tipo === 'n') { const n = Math.max(1, Math.floor(modo.n)); grupos = []; for (let i = 0; i < total; i += n) grupos.push(d.getPageIndices().slice(i, i + n)); }
  else grupos = gruposDePaginas(modo.spec, total);
  if (!grupos.length) throw new Error('paginas');
  const out = [];
  for (const g of grupos) {
    const ini = g[0] + 1, fim = g[g.length - 1] + 1;
    out.push({ rotulo: ini === fim ? `p${ini}` : `p${ini}-${fim}`, bytes: await copiar(d, g) });
  }
  return out;
}
export async function extrair(b: Uint8Array, paginas: number[]) {
  if (!paginas.length) throw new Error('paginas');
  return copiar(await carregar(b), paginas);
}
export async function remover(b: Uint8Array, paginas: number[]) {
  const d = await carregar(b);
  const tirar = new Set(paginas);
  const ficam = d.getPageIndices().filter(i => !tirar.has(i));
  if (!ficam.length) throw new Error('todas');
  return copiar(d, ficam);
}
export async function girar(b: Uint8Array, paginas: number[] | null, graus: number) {
  const d = await carregar(b);
  const alvo = new Set(paginas ?? d.getPageIndices());
  d.getPages().forEach((p, i) => { if (alvo.has(i)) p.setRotation(degrees((((p.getRotation().angle + graus) % 360) + 360) % 360)); });
  return salvar(d);
}
/** Nova ordem: cada item é uma página (índice) ou null (página em branco), com giro extra. */
export async function reorganizar(b: Uint8Array, ordem: { p: number | null; giro?: number }[]) {
  const d = await carregar(b);
  const out = await novo();
  const usadas = ordem.filter(o => o.p != null).map(o => o.p!) as number[];
  const copias = usadas.length ? await out.copyPages(d, usadas) : [];
  let k = 0;
  const ref = d.getPageCount() ? tamanhoVisivel(d.getPage(0)) : A4;
  for (const o of ordem) {
    if (o.p == null) { out.addPage(ref); continue; }
    const pg = out.addPage(copias[k++]);
    if (o.giro) pg.setRotation(degrees((((pg.getRotation().angle + o.giro) % 360) + 360) % 360));
  }
  if (!out.getPageCount()) throw new Error('todas');
  return salvar(out);
}

/** Várias páginas numa folha (2, 4, 6 ou 9). */
export async function layout(b: Uint8Array, porFolha: 2 | 4 | 6 | 9, folha: [number, number] = A4) {
  const d = await carregar(b);
  const out = await novo();
  const [col, lin] = porFolha === 2 ? [2, 1] : porFolha === 4 ? [2, 2] : porFolha === 6 ? [2, 3] : [3, 3];
  const [W, H] = porFolha === 2 ? [folha[1], folha[0]] : folha;
  const M = 18, G = 10;
  const cw = (W - 2 * M - (col - 1) * G) / col, ch = (H - 2 * M - (lin - 1) * G) / lin;
  const emb = await out.embedPages(d.getPages());
  for (let i = 0; i < emb.length; i += porFolha) {
    const pg = out.addPage([W, H]);
    emb.slice(i, i + porFolha).forEach((e, j) => {
      const c = j % col, l = Math.floor(j / col);
      const k = Math.min(cw / e.width, ch / e.height);
      const w = e.width * k, h = e.height * k;
      pg.drawPage(e, { x: M + c * (cw + G) + (cw - w) / 2, y: H - M - (l + 1) * ch - l * G + (ch - h) / 2, width: w, height: h });
    });
  }
  return salvar(out);
}

/** Muda o tamanho do papel (A4, Carta...), encaixando o conteúdo sem cortar. */
export async function tamanhoPagina(b: Uint8Array, alvo: [number, number]) {
  const d = await carregar(b);
  const out = await novo();
  const emb = await out.embedPages(d.getPages());
  emb.forEach((e, i) => {
    const deitada = tamanhoVisivel(d.getPage(i))[0] > tamanhoVisivel(d.getPage(i))[1];
    const [W, H] = deitada ? [alvo[1], alvo[0]] : alvo;
    const pg = out.addPage([W, H]);
    const k = Math.min(W / e.width, H / e.height);
    pg.drawPage(e, { x: (W - e.width * k) / 2, y: (H - e.height * k) / 2, width: e.width * k, height: e.height * k });
  });
  return salvar(out);
}

/** Corta as margens (porcentagem de cada lado). */
export async function recortar(b: Uint8Array, m: { sup: number; inf: number; esq: number; dir: number }, paginas: number[] | null) {
  const d = await carregar(b);
  const alvo = new Set(paginas ?? d.getPageIndices());
  d.getPages().forEach((p, i) => {
    if (!alvo.has(i)) return;
    const { width: w, height: h } = p.getSize();
    const x = (w * m.esq) / 100, y = (h * m.inf) / 100;
    const cw = w * (1 - (m.esq + m.dir) / 100), chh = h * (1 - (m.sup + m.inf) / 100);
    if (cw < 20 || chh < 20) return;
    p.setCropBox(x, y, cw, chh); p.setMediaBox(x, y, cw, chh);
  });
  return salvar(d);
}

/** Todas as páginas, uma embaixo da outra, numa página só (bom para ler no celular). */
export async function paginaUnica(b: Uint8Array) {
  const d = await carregar(b);
  const out = await novo();
  const emb = await out.embedPages(d.getPages());
  const W = Math.max(...emb.map(e => e.width));
  let H = emb.reduce((s, e) => s + e.height * (W / e.width), 0);
  const k = H > 14400 ? 14400 / H : 1;   // limite do formato PDF: 14.400 pontos
  H *= k;
  const pg = out.addPage([W * k, H]);
  let y = H;
  for (const e of emb) { const h = e.height * (W / e.width) * k; y -= h; pg.drawPage(e, { x: 0, y, width: W * k, height: h }); }
  return salvar(out);
}

// ─── Converter ───
export type ImgPdf = { bytes: Uint8Array; png?: boolean };
export async function imagensParaPdf(imgs: ImgPdf[], tamanho: 'a4' | 'carta' | 'foto', margem = 0) {
  const out = await novo();
  for (const im of imgs) {
    const e = im.png ? await out.embedPng(im.bytes) : await out.embedJpg(im.bytes);
    const deitada = e.width > e.height;
    let [W, H] = tamanho === 'carta' ? CARTA : A4;
    if (tamanho === 'foto') [W, H] = deitada ? [A4[1], (A4[1] * e.height) / e.width] : [A4[0], (A4[0] * e.height) / e.width];
    else if (deitada) [W, H] = [H, W];
    const pg = out.addPage([W, H]);
    const m = tamanho === 'foto' ? 0 : margem;
    const k = Math.min((W - 2 * m) / e.width, (H - 2 * m) / e.height);
    pg.drawImage(e, { x: (W - e.width * k) / 2, y: (H - e.height * k) / 2, width: e.width * k, height: e.height * k });
  }
  return salvar(out);
}

/** Páginas que viraram imagem (comprimir, efeito de scanner, tarja), no tamanho da página original. */
export async function paginasComoImagem(paginas: { jpg: Uint8Array; tamanho: [number, number]; texto?: string }[]) {
  const out = await novo();
  const fonte = await out.embedFont(StandardFonts.Helvetica);
  for (const p of paginas) {
    const e = await out.embedJpg(p.jpg);
    let [W, H] = p.tamanho;
    if ((e.width > e.height) !== (W > H)) [W, H] = [H, W];
    const pg = out.addPage([W, H]);
    pg.drawImage(e, { x: 0, y: 0, width: W, height: H });
    if (p.texto) textoInvisivel(pg, fonte, p.texto);
  }
  return salvar(out);
}

/** Texto escondido por baixo da página: dá para buscar e copiar no leitor de PDF. */
function textoInvisivel(pg: PDFPage, fonte: PDFFont, texto: string) {
  let y = pg.getHeight() - 20;
  for (const linha of limparTexto(texto).split('\n')) {
    if (y < 10) break;
    try { pg.drawText(linha.slice(0, 160), { x: 12, y, size: 7, font: fonte, opacity: 0 }); } catch { /* caractere fora da fonte */ }
    y -= 9;
  }
}
export async function comTextoInvisivel(b: Uint8Array, textos: string[]) {
  const d = await carregar(b);
  const fonte = await d.embedFont(StandardFonts.Helvetica);
  d.getPages().forEach((p, i) => { if (textos[i]) textoInvisivel(p, fonte, textos[i]); });
  return salvar(d);
}

// ─── Assinatura e segurança ───
function caixaDosTracos(tracos: Traco[]) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const tr of tracos) for (const m of tr.d.matchAll(/[ML](-?[\d.]+) (-?[\d.]+)/g)) {
    const x = +m[1], y = +m[2];
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return isFinite(x0) ? { x0, y0, x1: Math.max(x1, x0 + 1), y1: Math.max(y1, y0 + 1) } : null;
}
export async function assinar(b: Uint8Array, tracos: Traco[], paginas: number[], pos: Posicao, largura = 0.3, data?: string) {
  const d = await carregar(b);
  const cx = caixaDosTracos(tracos);
  if (!cx) throw new Error('assinatura');
  const fonte = await d.embedFont(StandardFonts.Helvetica);
  for (const i of paginas) {
    const pg = d.getPage(i); if (!pg) continue;
    const { width: W, height: H } = pg.getSize();
    let k = (W * largura) / (cx.x1 - cx.x0);
    k = Math.min(k, (H * 0.12) / (cx.y1 - cx.y0));
    const lw = (cx.x1 - cx.x0) * k, lh = (cx.y1 - cx.y0) * k + (data ? 12 : 0);
    const { x, y } = posicionar(W, H, lw, lh, pos);
    const topo = y + lh;
    for (const tr of tracos) pg.drawSvgPath(tr.d, { x: x - cx.x0 * k, y: topo + cx.y0 * k, scale: k, borderColor: rgb(0.06, 0.12, 0.45), borderWidth: Math.max(0.8, tr.w * k), borderLineCap: LineCapStyle.Round });
    if (data) pg.drawText(limparTexto(data), { x, y, size: 8, font: fonte, color: rgb(0.2, 0.2, 0.3) });
  }
  return salvar(d);
}

export async function carimbo(b: Uint8Array, texto: string, cor: string, paginas: number[], pos: Posicao, data?: string, tamanho = 22) {
  const d = await carregar(b);
  const f = await d.embedFont(StandardFonts.HelveticaBold);
  const fn = await d.embedFont(StandardFonts.Helvetica);
  const c = corRgb(cor);
  const t1 = limparTexto(texto).toUpperCase().slice(0, 40);
  for (const i of paginas) {
    const pg = d.getPage(i); if (!pg) continue;
    const { width: W, height: H } = pg.getSize();
    const tw = f.widthOfTextAtSize(t1, tamanho), dw = data ? fn.widthOfTextAtSize(data, tamanho * 0.42) : 0;
    const w = Math.max(tw, dw) + 24, h = tamanho + 18 + (data ? tamanho * 0.5 : 0);
    const { x, y } = posicionar(W, H, w, h, pos, 40);
    pg.drawRectangle({ x, y, width: w, height: h, borderColor: c, borderWidth: 2.5, opacity: 0, borderOpacity: 0.85, rotate: degrees(0) });
    pg.drawRectangle({ x: x + 4, y: y + 4, width: w - 8, height: h - 8, borderColor: c, borderWidth: 0.8, opacity: 0, borderOpacity: 0.85 });
    pg.drawText(t1, { x: x + (w - tw) / 2, y: y + h - tamanho - 8, size: tamanho, font: f, color: c, opacity: 0.9 });
    if (data) pg.drawText(data, { x: x + (w - dw) / 2, y: y + 9, size: tamanho * 0.42, font: fn, color: c, opacity: 0.9 });
  }
  return salvar(d);
}

export async function marcaDagua(b: Uint8Array, texto: string, cor: string, opacidade = 0.18, tamanho = 60) {
  const d = await carregar(b);
  const f = await d.embedFont(StandardFonts.HelveticaBold);
  const t1 = limparTexto(texto).slice(0, 60);
  for (const pg of d.getPages()) {
    const { width: W, height: H } = pg.getSize();
    const s = Math.min(tamanho, (Math.hypot(W, H) * 0.8) / Math.max(1, f.widthOfTextAtSize(t1, 1)));
    const tw = f.widthOfTextAtSize(t1, s);
    const ang = Math.atan2(H, W);
    // centraliza o texto girado na diagonal da página
    const x = W / 2 - (Math.cos(ang) * tw) / 2 + (Math.sin(ang) * s) / 3;
    const y = H / 2 - (Math.sin(ang) * tw) / 2 - (Math.cos(ang) * s) / 3;
    pg.drawText(t1, { x, y, size: s, font: f, color: corRgb(cor), opacity: opacidade, rotate: degrees((ang * 180) / Math.PI) });
  }
  return salvar(d);
}

/** Tarjas pretas (x, y, w, h em fração da página, a partir do canto de cima). */
export type Tarja = { pagina: number; x: number; y: number; w: number; h: number };
export async function tarjas(b: Uint8Array, lista: Tarja[]) {
  const d = await carregar(b);
  for (const r of lista) {
    const pg = d.getPage(r.pagina); if (!pg) continue;
    const [W, H] = [pg.getWidth(), pg.getHeight()];
    pg.drawRectangle({ x: r.x * W, y: H - (r.y + r.h) * H, width: r.w * W, height: r.h * H, color: rgb(0, 0, 0) });
  }
  return salvar(d);
}

/** Tira dados escondidos: autor, programas, comandos automáticos (JavaScript), anexos e (opcional) comentários. */
export async function higienizar(b: Uint8Array, tirarAnotacoes = false, tirarAnexos = true) {
  const d = await carregar(b);
  d.setTitle(''); d.setAuthor(''); d.setSubject(''); d.setKeywords([]); d.setCreator(''); d.setProducer('');
  const cat = d.catalog;
  for (const k of ['Metadata', 'OpenAction', 'AA', 'JavaScript']) cat.delete(PDFName.of(k));
  const nomes = cat.lookupMaybe(PDFName.of('Names'), PDFDict);
  if (nomes) { nomes.delete(PDFName.of('JavaScript')); if (tirarAnexos) nomes.delete(PDFName.of('EmbeddedFiles')); }
  for (const pg of d.getPages()) {
    pg.node.delete(PDFName.of('AA'));
    const anots = pg.node.lookupMaybe(PDFName.of('Annots'), PDFArray);
    if (!anots) continue;
    if (tirarAnotacoes) { pg.node.delete(PDFName.of('Annots')); continue; }
    for (let i = 0; i < anots.size(); i++) {
      const a = anots.lookupMaybe(i, PDFDict);
      const acao = a?.lookupMaybe(PDFName.of('A'), PDFDict);
      if (acao && String(acao.get(PDFName.of('S'))) === '/JavaScript') a!.delete(PDFName.of('A'));
      a?.delete(PDFName.of('AA'));
    }
  }
  return salvar(d);
}

export type Meta = { titulo: string; autor: string; assunto: string; palavras: string };
export async function lerMeta(b: Uint8Array): Promise<Meta> {
  const d = await carregar(b);
  return { titulo: d.getTitle() ?? '', autor: d.getAuthor() ?? '', assunto: d.getSubject() ?? '', palavras: d.getKeywords() ?? '' };
}
export async function gravarMeta(b: Uint8Array, m: Meta) {
  const d = await carregar(b);
  d.setTitle(m.titulo); d.setAuthor(m.autor); d.setSubject(m.assunto);
  d.setKeywords(m.palavras.split(/[,;]+/).map(s => s.trim()).filter(Boolean));
  d.setModificationDate(new Date());
  return salvar(d);
}

// ─── Editar ───
export type FormatoNumero = 'n' | 'n/t' | 'pag-n' | 'pag-n-de-t';
export async function numerarPaginas(b: Uint8Array, o: { formato: FormatoNumero; pos: Posicao; inicio: number; pularPrimeira: boolean; tamanho: number; palavraPagina: string; palavraDe: string }) {
  const d = await carregar(b);
  const f = await d.embedFont(StandardFonts.Helvetica);
  const pgs = d.getPages();
  const total = pgs.length - (o.pularPrimeira ? 1 : 0) + o.inicio - 1;
  pgs.forEach((pg, i) => {
    if (o.pularPrimeira && i === 0) return;
    const n = i - (o.pularPrimeira ? 1 : 0) + o.inicio;
    const txt = limparTexto(o.formato === 'n' ? `${n}` : o.formato === 'n/t' ? `${n} / ${total}` : o.formato === 'pag-n' ? `${o.palavraPagina} ${n}` : `${o.palavraPagina} ${n} ${o.palavraDe} ${total}`);
    const w = f.widthOfTextAtSize(txt, o.tamanho);
    const { x, y } = posicionar(pg.getWidth(), pg.getHeight(), w, o.tamanho, o.pos, 24);
    pg.drawText(txt, { x, y, size: o.tamanho, font: f, color: rgb(0.15, 0.15, 0.2) });
  });
  return salvar(d);
}

export async function adicionarTexto(b: Uint8Array, paginas: number[], texto: string, pos: Posicao, tamanho: number, cor: string, negrito: boolean) {
  const d = await carregar(b);
  const f = await d.embedFont(negrito ? StandardFonts.HelveticaBold : StandardFonts.Helvetica);
  const linhas = limparTexto(texto).split('\n').slice(0, 30);
  for (const i of paginas) {
    const pg = d.getPage(i); if (!pg) continue;
    const w = Math.max(...linhas.map(l => f.widthOfTextAtSize(l, tamanho))), h = linhas.length * tamanho * 1.25;
    const { x, y } = posicionar(pg.getWidth(), pg.getHeight(), w, h, pos);
    linhas.forEach((l, j) => pg.drawText(l, { x, y: y + h - (j + 1) * tamanho * 1.25 + tamanho * 0.25, size: tamanho, font: f, color: corRgb(cor) }));
  }
  return salvar(d);
}

export async function adicionarImagem(b: Uint8Array, img: ImgPdf, paginas: number[], pos: Posicao, largura: number, opacidade = 1) {
  const d = await carregar(b);
  const e: PDFImage = img.png ? await d.embedPng(img.bytes) : await d.embedJpg(img.bytes);
  for (const i of paginas) {
    const pg = d.getPage(i); if (!pg) continue;
    const w = pg.getWidth() * largura, h = (e.height / e.width) * w;
    const { x, y } = posicionar(pg.getWidth(), pg.getHeight(), w, h, pos);
    pg.drawImage(e, { x, y, width: w, height: h, opacity: opacidade });
  }
  return salvar(d);
}

export type Campo = { nome: string; tipo: 'texto' | 'marcar' | 'lista' | 'opcao'; valor: string; opcoes?: string[] };
export async function camposDoFormulario(b: Uint8Array): Promise<Campo[]> {
  const d = await carregar(b);
  const out: Campo[] = [];
  for (const c of d.getForm().getFields()) {
    const nome = c.getName();
    try {
      if (c instanceof PDFTextField) out.push({ nome, tipo: 'texto', valor: c.getText() ?? '' });
      else if (c instanceof PDFCheckBox) out.push({ nome, tipo: 'marcar', valor: c.isChecked() ? '1' : '' });
      else if (c instanceof PDFDropdown || c instanceof PDFOptionList) out.push({ nome, tipo: 'lista', valor: c.getSelected()[0] ?? '', opcoes: c.getOptions() });
      else if (c instanceof PDFRadioGroup) out.push({ nome, tipo: 'opcao', valor: c.getSelected() ?? '', opcoes: c.getOptions() });
    } catch { /* campo com defeito: pula */ }
  }
  return out;
}
export async function preencher(b: Uint8Array, valores: Record<string, string>, achatarDepois: boolean) {
  const d = await carregar(b);
  const form = d.getForm();
  for (const [nome, v] of Object.entries(valores)) {
    try {
      const c = form.getField(nome);
      if (c instanceof PDFTextField) c.setText(v);
      else if (c instanceof PDFCheckBox) { if (v) c.check(); else c.uncheck(); }
      else if ((c instanceof PDFDropdown || c instanceof PDFOptionList) && v) c.select(v);
      else if (c instanceof PDFRadioGroup && v) c.select(v);
    } catch { /* segue */ }
  }
  if (achatarDepois) form.flatten();
  return achatarDepois ? limpo(d) : salvar(d);
}
/** Depois de achatar, sobram referências a campos apagados: copiar as páginas para um PDF novo deixa só o que é usado. */
async function limpo(d: PDFDocument) {
  const out = await novo();
  for (const p of await out.copyPages(await carregar(await d.save({ useObjectStreams: false })), d.getPageIndices())) out.addPage(p);
  out.setTitle(d.getTitle() ?? ''); out.setAuthor(d.getAuthor() ?? '');
  return salvar(out);
}
/** Achatar: os campos do formulário viram parte da página (ninguém mais muda). */
export async function achatar(b: Uint8Array) {
  const d = await carregar(b);
  d.getForm().flatten();
  return limpo(d);
}
export async function removerAnotacoes(b: Uint8Array, manterLinks = true) {
  const d = await carregar(b);
  for (const pg of d.getPages()) {
    const anots = pg.node.lookupMaybe(PDFName.of('Annots'), PDFArray);
    if (!anots) continue;
    const novo = d.context.obj([]) as PDFArray;
    for (let i = 0; i < anots.size(); i++) {
      const a = anots.lookupMaybe(i, PDFDict);
      const tipo = String(a?.get(PDFName.of('Subtype')) ?? '');
      if ((manterLinks && tipo === '/Link') || tipo === '/Widget') novo.push(anots.get(i));
    }
    if (novo.size()) pg.node.set(PDFName.of('Annots'), novo); else pg.node.delete(PDFName.of('Annots'));
  }
  return salvar(d);
}
export async function anexar(b: Uint8Array, arquivos: { nome: string; bytes: Uint8Array; mime: string }[]) {
  const d = await carregar(b);
  for (const a of arquivos) await d.attach(a.bytes, a.nome, { mimeType: a.mime || 'application/octet-stream', creationDate: new Date(), modificationDate: new Date() });
  return salvar(d);
}

/** Abre e grava de novo: conserta muitos PDFs com a estrutura bagunçada. Também diminui alguns (compressão sem perda). */
export async function regravar(b: Uint8Array) { return salvar(await carregar(b)); }

export type Info = {
  paginas: number; tamanhos: string[]; titulo: string; autor: string; assunto: string; criador: string; produtor: string;
  criado: string; modificado: string; campos: number; anexos: boolean; protegido: boolean; bytes: number; versao: string;
};
export async function info(b: Uint8Array): Promise<Info> {
  const d = await carregar(b);
  const mm = (pt: number) => Math.round((pt * 25.4) / 72);
  const tamanhos = [...new Set(d.getPages().map(p => { const [w, h] = tamanhoVisivel(p); return `${mm(w)} × ${mm(h)} mm`; }))];
  const dt = (x?: Date) => (x ? x.toLocaleString() : '');
  let campos = 0; try { campos = d.getForm().getFields().length; } catch { /* sem formulário */ }
  const nomes = d.catalog.lookupMaybe(PDFName.of('Names'), PDFDict);
  const cab = new TextDecoder('latin1').decode(b.slice(0, 16)).match(/%PDF-(\d\.\d)/)?.[1] ?? '';
  return {
    paginas: d.getPageCount(), tamanhos, titulo: d.getTitle() ?? '', autor: d.getAuthor() ?? '', assunto: d.getSubject() ?? '',
    criador: d.getCreator() ?? '', produtor: d.getProducer() ?? '', criado: dt(d.getCreationDate()), modificado: dt(d.getModificationDate()),
    campos, anexos: !!nomes?.get(PDFName.of('EmbeddedFiles')), protegido: d.isEncrypted, bytes: b.length, versao: cab,
  };
}
export async function contarPaginas(b: Uint8Array) { return (await carregar(b)).getPageCount(); }
export async function tamanhosDasPaginas(b: Uint8Array): Promise<[number, number][]> { return (await carregar(b)).getPages().map(tamanhoVisivel); }
